/**
 * AddPropertyModal Component
 *
 * Modal for manually adding properties to the database.
 * Useful for adding test addresses for mailshot distribution testing.
 */

import React, { useState } from "react";
import PropTypes from "prop-types";
import { FaTimes, FaPlus, FaSpinner } from "react-icons/fa";
import { db, functions } from "../../firebase";
import { httpsCallable } from "firebase/functions";
import { useToast } from "./Toast";
import { PropertyRepository, Property } from "../../models";
import { generatePropertyHash } from "../../utils";
import "./AddPropertyModal.scss";

export const AddPropertyModal = ({ isOpen, onClose, onSuccess }) => {
  const toast = useToast();
  const propertyRepo = new PropertyRepository(db, functions); // Use new architecture
  const [creating, setCreating] = useState(false);
  const [formData, setFormData] = useState({
    address: "",
    postcode: "",
    town: "",
    price: "",
    propertyStatus: "For Sale",
    isTestProperty: false,
  });

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.address.trim()) {
      toast.warning("Please enter an address");
      return;
    }

    if (!formData.postcode.trim()) {
      toast.warning("Please enter a postcode");
      return;
    }

    try {
      setCreating(true);

      // Parse price if provided
      let parsedPrice = null;
      if (formData.price.trim()) {
        // Remove £ and commas, parse as number
        const priceStr = formData.price.replace(/[£,]/g, "").trim();
        if (!isNaN(priceStr) && priceStr !== "") {
          parsedPrice = parseInt(priceStr, 10);
        }
      }

      // Generate address hash
      const addressHash = generatePropertyHash({
        address: formData.address,
        postcode: formData.postcode,
      });

      // Generate normalized address for matching (matches backend logic)
      const addressNormalized = `${formData.address} ${formData.postcode}`
        .toLowerCase()
        .replace(/[^\w\s]/g, "") // Remove punctuation
        .replace(/\b(flat|apartment|apt|unit)\s*\d+\w?\b/gi, "") // Remove flat numbers
        .replace(/\s+/g, " ") // Normalize spaces
        .trim();

      // Create property using domain model
      const property = new Property({
        propertyId: `manual_${Date.now()}`, // Generate unique ID for manually added properties
        address: formData.address.trim(),
        postcode: formData.postcode.trim(),
        town: formData.town.trim() || null,
        price: parsedPrice,
        propertyStatus: formData.propertyStatus,
        isTestProperty: formData.isTestProperty,
        addressHash,
        addressNormalized,
        mailshotIds: [],
        sentHistory: [],
      });

      const createdProperty = await propertyRepo.create(property);

      // Automatically geocode the new property in the background
      try {
        const geocodeFunction = httpsCallable(
          functions,
          "geocodePropertyAddress"
        );
        await geocodeFunction({
          propertyId: createdProperty.id,
          address: formData.address.trim(),
          postcode: formData.postcode.trim(),
        });
      } catch (geocodeError) {
        // Don't fail property creation if geocoding fails
        console.warn("Failed to geocode property:", geocodeError);
      }

      toast.success(
        `Property added successfully${
          formData.isTestProperty ? " (Test Property)" : ""
        }`
      );

      // Reset form
      setFormData({
        address: "",
        postcode: "",
        town: "",
        price: "",
        propertyStatus: "For Sale",
        isTestProperty: false,
      });

      if (onSuccess) {
        onSuccess();
      }

      onClose();
    } catch (error) {
      console.error("Error adding property:", error);
      if (error.code === "permission-denied") {
        toast.error("Permission denied: Only admins can add properties.");
      } else {
        toast.error("Failed to add property: " + error.message);
      }
    } finally {
      setCreating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="add-property-modal-overlay" onClick={onClose}>
      <div className="add-property-modal" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={handleSubmit}>
          <div className="add-property-modal__header">
            <div className="add-property-modal__title">
              <FaPlus />
              <span>Add Property Manually</span>
            </div>
            <button
              type="button"
              className="add-property-modal__close"
              onClick={onClose}
            >
              <FaTimes />
            </button>
          </div>

          <div className="add-property-modal__body">
            <p className="subtitle">
              Add a property manually - useful for test addresses when checking
              mailshot distribution.
            </p>

            <div className="form-group">
              <label htmlFor="address">
                Address <span className="required">*</span>
              </label>
              <input
                id="address"
                type="text"
                value={formData.address}
                onChange={(e) => handleChange("address", e.target.value)}
                placeholder="e.g., 123 Main Street, London"
                required
                autoFocus
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="postcode">
                  Postcode <span className="required">*</span>
                </label>
                <input
                  id="postcode"
                  type="text"
                  value={formData.postcode}
                  onChange={(e) => handleChange("postcode", e.target.value)}
                  placeholder="e.g., SW1A 1AA"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="town">Town</label>
                <input
                  id="town"
                  type="text"
                  value={formData.town}
                  onChange={(e) => handleChange("town", e.target.value)}
                  placeholder="e.g., London"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="price">Price</label>
                <input
                  id="price"
                  type="text"
                  value={formData.price}
                  onChange={(e) => handleChange("price", e.target.value)}
                  placeholder="e.g., £250,000 or 250000"
                />
                <small className="help-text">
                  Enter as number (250000) or with £ and commas (£250,000)
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="status">Status</label>
                <select
                  id="status"
                  value={formData.propertyStatus}
                  onChange={(e) =>
                    handleChange("propertyStatus", e.target.value)
                  }
                >
                  <option value="For Sale">For Sale</option>
                  <option value="Sold STC">Sold STC</option>
                  <option value="Under Offer">Under Offer</option>
                </select>
              </div>
            </div>

            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.isTestProperty}
                  onChange={(e) =>
                    handleChange("isTestProperty", e.target.checked)
                  }
                />
                <span>Mark as Test Property</span>
              </label>
              <small className="help-text">
                Test properties are used for checking mailshot distribution and
                can be filtered separately
              </small>
            </div>
          </div>

          <div className="add-property-modal__footer">
            <button
              type="button"
              className="btn btn--secondary"
              onClick={onClose}
              disabled={creating}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn--primary"
              disabled={creating}
            >
              {creating ? (
                <>
                  <FaSpinner className="spinner" /> Adding...
                </>
              ) : (
                <>
                  <FaPlus /> Add Property
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

AddPropertyModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};
